import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import I10n from '../../features/i10n';
import DropdownMenu from '../dropdown_menu';
import InformationIcon from '../../atoms/information-icon';
import DynamicLabel from '../../atoms/dynamic-label';
import MsxIcons from '../../features/msx-icons';
import { SECONDARY_COLOR } from '../../color-constants';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  color: #626469;
  padding-top: 10px;
`;

const TitleRow = styled.div`
  display: flex;
  font-size: 24px;
  align-items: center;
  margin-bottom: 10px;
`;

const Title = styled.span`
  font-weight: bold;
`;

const InfoIconWrapper = styled.div`
  color: #BFBFBF;
  margin-left: 10px;
  cursor: pointer;
`;

const Hint = styled.div`
  color: #1F2729;
  padding: 10px;
  min-width: 200px;
  font-size: 14px;
`;

const TotalConsumptionRow = styled.div`
  display: flex;
  font-size: 18px;
  margin-bottom: 10px;
`;

const Subtitle = styled.span`
  font-size: 18px;
  font-weight: bold;
`;

const TotalConsumptionValue = styled(Subtitle)`
  color: ${SECONDARY_COLOR};
  color: var(--se-primary, ${SECONDARY_COLOR});
  margin-left: 10px;
`;

const ConsumptionGroupsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  @media (max-width: 450px) {
    flex-direction: column;
  }
`;

const ConsumptionGroupWrapper = styled.div`
  display: flex;
  flex: 1 1 0px;
  flex-direction: column;
  margin-right: 10px;
  margin-bottom: 10px;
  min-width: 300px;
  width: 300px;
  max-width: 300px;
  @media (max-width: 450px) {
    min-width: auto;
    width: auto;
    max-width: unset;
  }
`;

const ConsumptionGroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${SECONDARY_COLOR};
  background-color: var(--se-primary, ${SECONDARY_COLOR});
  color: white !important;
  color: var(--se-primary-contrast, white) !important;
  font-weight: bold;
  padding: 10px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  min-height: 24px;
  ${props => props.hasPowerSupplyCapcityExceeded && `
  border-top: 2px solid #E2B80F;
  border-left: 2px solid #E2B80F;
  border-right: 2px solid #E2B80F;
  `}
`;

const ConsumptionGroupTitle = styled.span``;

const ConsumptionGroupContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  font-size: 12px;
  border: 1px solid #E7E6E6;
  padding: 10px 10px 0px 10px;
  min-height: 200px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  ${props => props.hasPowerSupplyCapcityExceeded && `
    border-left: 2px solid #E2B80F;
    border-right: 2px solid #E2B80F;
    border-bottom: 2px solid #E2B80F;
  `}

`;

const ConsumptionGroupRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
`;

const OutputsConsumptionRow = styled(ConsumptionGroupRow)`
  font-weight: bold;
  align-items: center;
  min-height: 40px;
`;

const OutputEditionWrapper = styled.div`
  display: flex;
  margin-left: 10px;
`;

const ConsumptionCategory = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
`;

const ConsumptionCategoryTitle = styled.span`
  font-weight: bold;
  margin-bottom: 10px;
`;

const ConsumptionCategoryContent = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 10px;
`;

const Text = styled.span``;

const ConsumptionValue = styled(Text)`
  color: ${SECONDARY_COLOR};
  color: var(--se-primary, ${SECONDARY_COLOR});
  white-space: nowrap;
`;

const WarningIconWrapper = styled.div`
  cursor: pointer;
`;

const NotificationWrapper = styled.div`
  position: fixed;
  top : 110px;
  left: 10px;
  right: 10px;
  z-index: 400;
  display: flex;
  align-items: center;
  padding: 5px;
  color: white;
  background-color: #E47F00;
  font-size: 14px;
  border-radius: 5px;
  margin-bottom: 10px;
`;

const NotificationText = styled.span`
  margin-left: 5px;
`;

class OutputConsumptionStep extends React.Component {

  static propTypes = {
    consumptionGroups: PropTypes.array,
    candidatePowerSupplies: PropTypes.array,
    updateGroupConsumption: PropTypes.func,
    isLoading: PropTypes.bool
  }

  constructor(props) {
    super(props);
    this._isMounted = false;
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  updateStateIfMounted(objectToAffect) {
    if (this._isMounted) {
      this.setState(objectToAffect);
    }
  }

  get maxCandidatePowerSupplyCapacity() {
    let result = 0;
    this.props.candidatePowerSupplies.forEach(powerSupply => {
      if ((powerSupply.powerSupply || 0) > result) {
        result = powerSupply.powerSupply;
      }

    });

    return result;
  }

  get anyIslandExceedsMaxPowerSupplyCapacity() {
    let result = false;
    let max = this.maxCandidatePowerSupplyCapacity;
    for (let i = 0; i < this.props.consumptionGroups.length && !result; i++) {
      if (this.props.consumptionGroups[i].groupTotalConsumption > max) {
        result = true;
      }
    }

    return result;
  }

  get totalConsumption() {
    let result = 0;
    this.props.consumptionGroups.forEach(group => {
      result += ((group.controllerConsumption || 0) + (group.controllerInputsConsumption || 0) + group.modulesConsumption + group.modulesInputsConsumption + group.outputConsumption);
    });

    return result;
  }

  renderElectricalValueString = (inputValue) => {
    const value = parseFloat(inputValue);
    const shouldScaleConsumption = (value >= 1000.0);
    const consumptionUnit = shouldScaleConsumption ? 'A' : 'mA';

    return shouldScaleConsumption ? `${(value / 1000.0).toFixed(2)} ${consumptionUnit}` : `${parseFloat(value.toFixed(2))} ${consumptionUnit}`;
  }

  render = () => {
    let maxPowerSupplyCapacity = this.maxCandidatePowerSupplyCapacity;
    return (
      <Container>
        <TitleRow>
          <Title>{I10n.get('screen.power_supply_screen.output_consumption_step.label')}</Title>
          <DropdownMenu
            data-clickable
            toggleElement={<InfoIconWrapper slot="trigger"><InformationIcon size="small"></InformationIcon>
            </InfoIconWrapper>}
            containerElement={<Hint>{I10n.get('screen.power_supply_screen.output_consumption_step.hint')}</Hint>}
            forceLeft
          >
          </DropdownMenu>

        </TitleRow>
        {!this.props.isLoading && this.anyIslandExceedsMaxPowerSupplyCapacity && <NotificationWrapper>
          <MsxIcons name="alert"></MsxIcons>
          <NotificationText>{I10n.get('notification.no_power_supply')}</NotificationText>
        </NotificationWrapper>}
        <TotalConsumptionRow>
          <Subtitle>{I10n.get('screen.power_supply_screen.total_consumption')}</Subtitle>
          <TotalConsumptionValue>{this.renderElectricalValueString(this.totalConsumption)}</TotalConsumptionValue>
        </TotalConsumptionRow>
        <ConsumptionGroupsWrapper>
          {this.props.consumptionGroups.map((consumptionGroup, idx) => {
            const hasPowerSupplyCapcityExceeded = (!this.props.isLoading && consumptionGroup.groupTotalConsumption > maxPowerSupplyCapacity);
            return (
              <ConsumptionGroupWrapper key={idx}>
                <ConsumptionGroupHeader hasPowerSupplyCapcityExceeded={hasPowerSupplyCapcityExceeded}>
                  <ConsumptionGroupTitle>
                    {consumptionGroup.slot === 0 ? I10n.get('screen.head_device_selector_screen.plc') : consumptionGroup.name}
                  </ConsumptionGroupTitle>
                  {hasPowerSupplyCapcityExceeded && <WarningIconWrapper><MsxIcons name="alert"></MsxIcons></WarningIconWrapper>}
                </ConsumptionGroupHeader>
                <ConsumptionGroupContent hasPowerSupplyCapcityExceeded={hasPowerSupplyCapcityExceeded}>
                  {consumptionGroup.slot === 0 && <ConsumptionCategory>
                    <ConsumptionCategoryTitle>{I10n.get('screen.power_supply_screen.controller_consumption')}</ConsumptionCategoryTitle>
                    <ConsumptionCategoryContent>
                      <ConsumptionGroupRow>
                        <Text>{I10n.get('screen.power_supply_screen.consumed_controller')}
                        </Text>
                        <ConsumptionValue>{this.renderElectricalValueString(consumptionGroup.controllerConsumption)}</ConsumptionValue>
                      </ConsumptionGroupRow>
                      <ConsumptionGroupRow>
                        <Text>{I10n.get('screen.power_supply_screen.consumed_inputs')}
                        </Text>
                        <ConsumptionValue>{this.renderElectricalValueString(consumptionGroup.controllerInputsConsumption)}</ConsumptionValue>
                      </ConsumptionGroupRow>
                    </ConsumptionCategoryContent>
                  </ConsumptionCategory>}
                  <ConsumptionCategory>
                    <ConsumptionCategoryTitle>{I10n.get('screen.power_supply_screen.consumed_modules')}</ConsumptionCategoryTitle>
                    <ConsumptionCategoryContent>
                      <ConsumptionGroupRow>
                        <Text>
                          {I10n.get('screen.power_supply_screen.consumed_modules')}
                        </Text>
                        <ConsumptionValue>{this.renderElectricalValueString(consumptionGroup.modulesConsumption)}</ConsumptionValue>
                      </ConsumptionGroupRow>
                      <ConsumptionGroupRow>
                        <Text>{I10n.get('screen.power_supply_screen.consumed_inputs')}</Text>
                        <ConsumptionValue>{this.renderElectricalValueString(consumptionGroup.modulesInputsConsumption)}</ConsumptionValue>
                      </ConsumptionGroupRow>
                    </ConsumptionCategoryContent>
                  </ConsumptionCategory>
                  <OutputsConsumptionRow>
                    <Text>{I10n.get('screen.power_supply_screen.consumption_of_outputs')}</Text>
                    <OutputEditionWrapper>
                      <DynamicLabel
                        value={'' + consumptionGroup.outputConsumption}
                        renderValue={v => `${this.renderElectricalValueString(v)}`}
                        setValue={v => {
                          try {
                            let value = parseInt(v, 10);
                            if (isNaN(value)) {
                              value = 0;
                            }
                            this.props.updateGroupConsumption(consumptionGroup.slot, Math.max(value, 0));
                          } catch (err) { }
                        }}
                        enableEdit={!this.props.isLoading}
                        withCancel={true}
                        customInputProps={{
                          type: "number", name: "consumptionDevices",
                          min: "0", step: "10",
                          color: 'primary'
                        }}
                        editHintText={`(${I10n.get('unit.ma', 'data')})`}
                      ></DynamicLabel>
                    </OutputEditionWrapper>

                  </OutputsConsumptionRow>

                </ConsumptionGroupContent>

              </ConsumptionGroupWrapper>
            );
          })}
        </ConsumptionGroupsWrapper>
      </Container>
    );
  }
}

export default OutputConsumptionStep;
